import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { checkClaim } from "@/lib/api";

/**
 * Public Claim Reality Check input box.
 *
 * History/results live in PeopleAreSayingCard (the public, deduplicated
 * library). After a successful check, this calls onChecked() so the parent
 * can refresh that list.
 */
export default function RealityCheckCard({ measureDbId, onChecked }) {
  const [claim, setClaim] = useState("");
  const [claimSource, setClaimSource] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = claim.trim();
    if (!trimmed) {
      toast.error("Paste a claim to check.");
      return;
    }
    setBusy(true);
    try {
      const result = await checkClaim(measureDbId, trimmed, claimSource.trim());
      setClaim("");
      setClaimSource("");
      if (result.classification === "error") {
        toast.error(result.error || "Claim check failed.");
      } else if (result.from_cache) {
        toast.success("Previously checked claim — showing the existing verdict.");
      } else {
        toast.success("Claim checked and added to People Are Saying.");
      }
      if (typeof onChecked === "function") onChecked();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Claim check failed.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="paper-card-elevated rounded-sm p-5 mb-5"
      data-testid="reality-check-card"
    >
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-dashed border-[hsl(var(--rule))]">
        <Search className="w-5 h-5 text-[hsl(var(--ink))]" />
        <div className="section-label">Public Claim Reality Check</div>
      </div>

      <p className="text-xs text-[hsl(var(--ink-muted))] leading-relaxed mb-3 max-w-3xl">
        Paste a claim you saw in a video, article, campaign ad, social post,
        mailer, or conversation. Measure X-Ray will check it against the
        official bill text and add the verified result to <em>People Are
        Saying</em> above for future users.
      </p>

      <form onSubmit={submit} className="space-y-2">
        <textarea
          data-testid="reality-check-claim-input"
          value={claim}
          onChange={(e) => setClaim(e.target.value)}
          placeholder='Example: "AB 130 creates a $324,000 housing tax."'
          rows={3}
          maxLength={4000}
          className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))] resize-y"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <input
            data-testid="reality-check-source-input"
            value={claimSource}
            onChange={(e) => setClaimSource(e.target.value)}
            placeholder="Optional public URL (article, post, video) — usernames or private notes are not stored"
            className="flex-1 min-w-[200px] bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue))]"
          />
          <button
            type="submit"
            disabled={busy || !claim.trim()}
            data-testid="reality-check-submit"
            className="pill-btn pill-btn-primary disabled:opacity-50"
          >
            {busy ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Checking…
              </>
            ) : (
              <>Check claim</>
            )}
          </button>
        </div>
        <p className="text-[11px] text-[hsl(var(--ink-muted))] leading-relaxed">
          Privacy: only public URLs are stored as a reference link. Free-text
          notes, usernames, and screenshots are never stored or displayed.
        </p>
      </form>
    </section>
  );
}
